import {useSortable} from '@dnd-kit/sortable';
import {Overlay} from 'platform/components';
import {Box, Center, Icon, Show} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {DashboardTile} from '../../../types/DashboardTile';
import {DashboardTileId} from '../../../types/DashboardTileId';
import {DashboardTileSize} from '../../../types/DashboardTileSize';
import {DashboardTileContent} from './DashboardTileContent';
import {DashboardTileToolbar} from './DashboardTileToolbar';

interface SortableDashboardTileProps extends TestIdProps {
  tile: DashboardTile;
  isCustomising: boolean;
  isVacating: boolean;
  onResize: (id: DashboardTileId, size: DashboardTileSize) => void;
  onHide: (id: DashboardTileId) => void;
}

const VACATING_OPACITY = 0.4;
const VACATING_TRANSITION = '150ms ease-in-out';

const getTileCursor = (isCustomising: boolean, isDragging: boolean) =>
  match({isCustomising, isDragging})
    .with({isDragging: true}, always('grabbing'))
    .with({isCustomising: true}, always('grab'))
    .otherwise(always('auto'));

interface StyledTileWrapperProps {
  $isDragging: boolean;
  $isCustomising: boolean;
}

const StyledTileWrapper = styled.div<StyledTileWrapperProps>`
  height: 100%;
  min-width: 0;
  position: relative;
  isolation: isolate;
  cursor: ${({$isCustomising, $isDragging}) => getTileCursor($isCustomising, $isDragging)};
  touch-action: ${({$isCustomising}) => ($isCustomising ? 'none' : 'auto')};
`;

interface StyledTileContentProps {
  $isInert: boolean;
  $isDragSource: boolean;
  $isVacating: boolean;
}

const StyledTileContent = styled.div<StyledTileContentProps>`
  height: 100%;
  /* Contains inner z-indexes that would otherwise escape above the customise overlay. */
  isolation: ${({$isInert}) => ($isInert ? 'isolate' : 'auto')};
  pointer-events: ${({$isInert}) => ($isInert ? 'none' : 'auto')};
  user-select: ${({$isInert}) => ($isInert ? 'none' : 'auto')};
  visibility: ${({$isDragSource}) => ($isDragSource ? 'hidden' : 'visible')};
  /* Fades rather than moves: the droppable rect must stay where collision detection found it. */
  opacity: ${({$isVacating}) => ($isVacating ? VACATING_OPACITY : 1)};
  transition: opacity ${VACATING_TRANSITION};
`;

const StyledDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({theme}) => theme.getSize(12)};
  height: ${({theme}) => theme.getSize(12)};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.general.white};
  box-shadow: ${({theme}) => theme.shadows.elevation_2};
`;

const StyledPlaceholderFrame = styled.div`
  position: absolute;
  inset: 0;
  border: ${({theme}) => theme.getSize(1)} dashed
    ${({theme}) => theme.colors.palettes.neutral[70][100]};
  border-radius: ${({theme}) => theme.radii.medium};
  background: ${({theme}) => theme.colors.palettes.neutral[70][40]};
`;

export function SortableDashboardTile(props: SortableDashboardTileProps) {
  const {attributes, listeners, setNodeRef, isDragging} = useSortable({
    id: props.tile.id,
    disabled: !props.isCustomising,
  });

  const dragProps = props.isCustomising ? {...attributes, ...listeners} : undefined;

  return (
    <StyledTileWrapper
      ref={setNodeRef}
      $isDragging={isDragging}
      $isCustomising={props.isCustomising}
      aria-label={
        props.isCustomising ? i18n.t('page.homepage.dashboardLayout.moveTile') : undefined
      }
      {...dragProps}
      data-dashboard-tile-id={props.tile.id}
      data-testid={props['data-testid']}
    >
      {/* `inert` takes the covered controls out of the tab order and hides them from assistive
          technology; the drag handle and the tile toolbar sit outside this subtree. */}
      <StyledTileContent
        inert={props.isCustomising}
        $isInert={props.isCustomising}
        $isDragSource={isDragging}
        $isVacating={props.isVacating}
      >
        <DashboardTileContent tile={props.tile} />
      </StyledTileContent>
      <Show when={props.isCustomising && !isDragging}>
        {/* zIndex="base" keeps the Overlay (default 9100) out of the page's own stacking contexts. */}
        <Overlay variant="neutral" zIndex="base" data-testid={suffixTestId('tileOverlay', props)}>
          <Center height="100%">
            <StyledDragHandle>
              <Icon value="action/drag_indicator" size={8} color="text.primary" />
            </StyledDragHandle>
          </Center>
          <Box position="absolute" top={2} right={2}>
            <DashboardTileToolbar
              tileId={props.tile.id}
              size={props.tile.size}
              onResize={props.onResize}
              onHide={props.onHide}
              data-testid={props['data-testid']}
            />
          </Box>
        </Overlay>
      </Show>
      <Show when={isDragging}>
        <StyledPlaceholderFrame data-testid={suffixTestId('tilePlaceholder', props)} />
      </Show>
    </StyledTileWrapper>
  );
}
